﻿import React, { useEffect } from 'react';

import { ActivityHistory } from 'pages/account/feature_flag/components/FeatureFlagProfile/ActivityHistory';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { useFeatureFlagDetails } from 'pages/account/feature_flag/context';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { TileContainer } from 'components/TileContainer/TileContainer';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FeatureFlag, FeatureFlagApi } from 'apis/FeatureFlagApi';
import { EarlyAccessRing } from 'apis/EarlyAccessRingApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { Col, Row } from 'react-bootstrap';
import { useSuperState } from 'hooks';

import 'pages/account/feature_flag/components/FeatureFlagProfile/styles/feature-flag-profile.scss';

interface FeatureFlagProfileProps {
	earlyAccessRings: EarlyAccessRing[];
	mode?: 'Create' | 'Edit';
	onDetailsItemClick: (dataItem: FeatureFlag, isNewFeatureFlag: boolean, updateGlobalState: boolean) => Promise<void>;
	onFeatureFlagNotification: (newOrUpdatedFeatureFlag: FeatureFlag) => void;
}

export function FeatureFlagProfile(props: FeatureFlagProfileProps) {

    const { earlyAccessRings, mode, onDetailsItemClick, onFeatureFlagNotification } = props;

    const [state, setState] = useSuperState({
        featureFlagComment: '' as string,
        isFeatureFlagCommentValid: true,
        isNameValid: true,
        isRingValid: true
    });
    const { featureFlagComment, isFeatureFlagCommentValid, isNameValid, isRingValid } = state;

    const { formatMessage } = useIntl();
    const { catchWithToast } = useNotification();
    const { pageContext, update } = useFeatureFlagDetails();

    useEffect(() => {

        if (mode === 'Create') {

            setState({ featureFlagComment: formatMessage({ id: 'creating_feature_flag.ffa936cd', defaultMessage: 'Creating Feature Flag.' }) });

        } else {

            setState({ featureFlagComment: '' });

        }

    }, []);

    const createFeatureFlag = async () => {

        if (pageContext?.Data.featureFlagDetails) {

            try {

                const newFeatureFlagId = await FeatureFlagApi.Create(
                    featureFlagComment,
                    pageContext.Data.featureFlagDetails.Name,
                    pageContext.Data.featureFlagDetails.EarlyAccessRing.Id,
                    pageContext.Data.featureFlagDetails.ReleaseNotes,
                    pageContext.Data.featureFlagDetails.Description,
                    pageContext.Data.featureFlagDetails.DisplayName
                );
                if (newFeatureFlagId && pageContext.Data.featureFlagDetails) {

                    onDetailsItemClick(
                        { ...pageContext.Data.featureFlagDetails, Id: newFeatureFlagId },
                        true,
                        false
                    );
                    onFeatureFlagNotification({
                        ...pageContext.Data.featureFlagDetails,
                        CreateDate: new Date().toISOString(),
                        Id: newFeatureFlagId
                    });

                }

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_create_feature_flag.1f63f825', defaultMessage: 'Failed to create feature flag.' }))(e);

            }

        }

    };

    const updateFeatureFlag = async () => {

        const isFeatureFlagCommentValid = featureFlagComment.length > 0;
        setState({ isFeatureFlagCommentValid });
        if (pageContext?.Data.featureFlagDetails && isFeatureFlagCommentValid) {

            try {

                await FeatureFlagApi.Update(
                    featureFlagComment,
                    pageContext.Data.featureFlagDetails.Id,
                    pageContext.Data.featureFlagDetails.EarlyAccessRing.Id,
                    pageContext.Data.featureFlagDetails.ReleaseNotes,
                    pageContext.Data.featureFlagDetails.Description,
                    pageContext.Data.featureFlagDetails.DisplayName
                );
                if (pageContext.Data.featureFlagDetails) {

                    onDetailsItemClick(pageContext.Data.featureFlagDetails, false, true);
                    onFeatureFlagNotification(pageContext.Data.featureFlagDetails);

                }

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_update_feature_flag.b813b48b', defaultMessage: 'Failed to update feature flag.' }))(e);

            }

        }

    };

    const onSaveClick = () => {

        if (pageContext?.Data.featureFlagDetails) {

            const nameIsValid = pageContext.Data.featureFlagDetails.Name.length > 0;
            const ringIsValid = pageContext.Data.featureFlagDetails.EarlyAccessRing.Name.length > 0;
            setState({ isNameValid: nameIsValid, isRingValid: ringIsValid });
            if (nameIsValid && ringIsValid) {

                switch (mode) {

                    case 'Create':
                        createFeatureFlag();
                        break;
                    case 'Edit':
                        updateFeatureFlag();

                }
                setState({ featureFlagComment: '' });

            }

        }

    };

    return (
        <>
            <Row>
                <Col className="d-flex justify-content-end">
                    {(!isRingValid || !isNameValid || !isFeatureFlagCommentValid) && (
                        <div className="text-align-center mb-4 feature-flag-profile__required-text">
                            <FormattedMessage defaultMessage="*Please fill out all required fields." id="please_fill_out_all_required_fields.85717bfd" />
                        </div>
                    )}
                </Col>
            </Row>
            {pageContext?.Data.featureFlagDetails && (
                <TileContainer
                    addButton
                    addButtonCustom={(
                        <GMetrixButton onClick={onSaveClick} variant="primary">
                            {mode === 'Create' ? <FormattedMessage defaultMessage="Create Feature Flag" id="create_feature_flag.5210e077" /> : <FormattedMessage defaultMessage="Save" id="save.d44082ae" />}
                        </GMetrixButton>
                    )}
                >
                    <Row>
                        <Col className="col-12 col-md-4 mb-3">
                            <GMetrixInput
                                required
                                onChange={e => {

                                    if (pageContext.Data.featureFlagDetails) {

                                        update({
                                            featureFlagDetails: {
                                                ...pageContext.Data.featureFlagDetails,
                                                Name: e.target.value
                                            }
                                        });

                                    }

                                }}
                                label={formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' })}
                                readOnly={mode !== 'Create'}
                                value={pageContext.Data.featureFlagDetails.Name}
                            />
                        </Col>
                        <Col className="col-12 col-md-4 mb-3">
                            <GMetrixInput
                                onChange={e => {

                                    if (pageContext.Data.featureFlagDetails) {

                                        update({
                                            featureFlagDetails: {
                                                ...pageContext.Data.featureFlagDetails,
                                                DisplayName: e.target.value
                                            }
                                        });

                                    }

                                }}
                                label={formatMessage({ id: 'display_name.1ed21d07', defaultMessage: 'Display Name' })}
                                value={pageContext.Data.featureFlagDetails.DisplayName}
                            />
                        </Col>
                        <Col className="col-12 col-md-4 mb-3">
                            <GMetrixDropdown
                                required
                                onChange={dataItem => {

                                    if (pageContext.Data.featureFlagDetails) {

                                        update({
                                            featureFlagDetails: {
                                                ...pageContext.Data.featureFlagDetails,
                                                EarlyAccessRing: dataItem
                                            }
                                        });

                                    }

                                }}
                                dataItemKey="Id"
                                dataItems={earlyAccessRings}
                                isValid={isRingValid}
                                label={formatMessage({ id: 'ring.0c682750', defaultMessage: 'Ring' })}
                                textField="Name"
                                value={pageContext.Data.featureFlagDetails.EarlyAccessRing}
                            />
                        </Col>
                        <Col className="col-12 col-md-4 mb-3">
                            <GMetrixInput
                                onChange={e => {

                                    if (pageContext.Data.featureFlagDetails) {

                                        update({
                                            featureFlagDetails: {
                                                ...pageContext.Data.featureFlagDetails,
                                                ReleaseNotes: e.target.value as string
                                            }
                                        });

                                    }

                                }}
                                className="feature-flag-profile__text-areas"
                                label={formatMessage({ id: 'release_notes.dbd9d217', defaultMessage: 'Release Notes' })}
                                value={pageContext.Data.featureFlagDetails.ReleaseNotes}
                            />

                        </Col>
                        <Col className="col-12 col-md-4 mb-3">
                            <GMetrixInput
                                onChange={e => {

                                    if (pageContext.Data.featureFlagDetails) {

                                        update({
                                            featureFlagDetails: {
                                                ...pageContext.Data.featureFlagDetails,
                                                Description: e.target.value as string
                                            }
                                        });

                                    }

                                }}
                                className="feature-flag-profile__text-areas"
                                label={formatMessage({ id: 'description.6317db73', defaultMessage: 'Description' })}
                                value={pageContext.Data.featureFlagDetails.Description}
                            />
                        </Col>
                        <Col className="col-12 col-md-4 mb-3">
                            <GMetrixInput
                                required
                                className="feature-flag-profile__text-areas"
                                label={formatMessage({ id: 'comment.e2d25fe4', defaultMessage: 'Comment' })}
                                onChange={e => setState({ featureFlagComment: e.target.value as string })}
                                readOnly={mode === 'Create'}
                                value={featureFlagComment}
                            />
                        </Col>
                    </Row>
                </TileContainer>
            )}
            {mode !== 'Create' && pageContext?.Data.featureFlagDetails && (
                <ActivityHistory featureFlagData={pageContext.Data.featureFlagDetails} />
            )}
        </>
    );

}
